import React, { useEffect, useState } from "react";
import {
  Button,
  IconButton,
  Box,
  Typography,
  Grid,
  CircularProgress,
  Chip,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TablePagination,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import axios from "axios";
import { BACKEND_BASE_URL } from "../config";
import { toast, ToastContainer } from "react-toastify";
import he from "he";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMagnifyingGlass } from "@fortawesome/free-solid-svg-icons";
import RefreshIcon from "@mui/icons-material/Refresh";

const statusColor = {
  pending: "default",
  retrying: "warning",
  escalated: "error",
  fixed: "success",
};

const ManageVideoSummaries = () => {
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({ total: 0, pending: 0, retrying: 0, escalated: 0 });
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [totalItems, setTotalItems] = useState(0);

  const fetchQueue = async (nextPage = page, nextRowsPerPage = rowsPerPage, nextSearch = searchQuery, nextStatus = statusFilter) => {
    setLoading(true);
    try {
      const response = await axios.get(`${BACKEND_BASE_URL}/api/video-summaries/recovery-queue/`, {
        params: {
          page: nextPage + 1,
          page_size: nextRowsPerPage,
          q: nextSearch,
          status: nextStatus,
        },
      });
      setItems(response.data?.items || []);
      setCounts(response.data?.counts || { total: 0, pending: 0, retrying: 0, escalated: 0, fixed: 0 });
      setTotalItems(response.data?.total_items || 0);
    } catch (error) {
      console.error("Error fetching summary recovery queue:", error);
      toast.error("Failed to load summary recovery queue.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchQueue();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchQueue(0, rowsPerPage, searchQuery, statusFilter);
      setPage(0);
    }, 350);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, statusFilter, rowsPerPage]);

  const runRecovery = async () => {
    setRunning(true);
    try {
      await axios.post(`${BACKEND_BASE_URL}/api/video-summaries/recovery-run/`, { max_items: 1000 });
      toast.success("Recovery task queued. Refreshing queue...");
      setTimeout(() => fetchQueue(page, rowsPerPage, searchQuery, statusFilter), 1500);
    } catch (error) {
      console.error("Error queuing recovery task:", error);
      toast.error("Failed to start recovery task.");
    } finally {
      setRunning(false);
    }
  };

  const handlePageChange = (_, newPage) => {
    setPage(newPage);
    fetchQueue(newPage, rowsPerPage, searchQuery, statusFilter);
  };

  const handleRowsPerPageChange = (event) => {
    const value = parseInt(event.target.value, 10);
    setRowsPerPage(value);
    setPage(0);
  };

  return (
    <>
      <ToastContainer />
      <Grid container spacing={4}>
        <Grid item xs={12} display="flex" justifyContent="space-between" alignItems="center">
          <Typography style={{ fontWeight: "bold" }} variant="h4">
            Summary Status
          </Typography>
          <Box display="flex" alignItems="center" flexWrap="nowrap">
            <TextField
              placeholder="Search..."
              variant="outlined"
              size="small"
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
              }}
              style={{ marginRight: "10px", minWidth: "220px", backgroundColor: "#fff" }}
              InputProps={{
                startAdornment: (
                  <FontAwesomeIcon
                    icon={faMagnifyingGlass}
                    style={{ marginRight: "8px", color: "#555" }}
                  />
                ),
              }}
            />
            <FormControl size="small" style={{ minWidth: "120px", marginRight: "10px", backgroundColor: "#fff" }}>
              <InputLabel id="summary-status-filter-label">Status</InputLabel>
              <Select
                labelId="summary-status-filter-label"
                value={statusFilter}
                label="Status"
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="retrying">Retrying</MenuItem>
                <MenuItem value="escalated">Escalated</MenuItem>
                <MenuItem value="fixed">Fixed</MenuItem>
              </Select>
            </FormControl>
            
            <Button
              variant="contained"
              style={{ backgroundColor: "#0B194A", color: "white", whiteSpace: "nowrap" }}
              onClick={runRecovery}
              disabled={loading || running}
            >
              {running ? "Running..." : "Run Recovery"}
            </Button>
          </Box>
        </Grid>

        <Grid item xs={12}>
          <Box display="flex" gap={1} marginBottom={2}>
            <Chip label={`Total: ${counts.total || 0}`} />
            <Chip label={`Pending: ${counts.pending || 0}`} />
            <Chip label={`Retrying: ${counts.retrying || 0}`} color="warning" />
            <Chip label={`Escalated: ${counts.escalated || 0}`} color="error" />
          </Box>

          {loading ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
              <CircularProgress />
            </Box>
          ) : items.length === 0 ? (
            <Typography variant="h6">No recoverable video summaries pending.</Typography>
          ) : (
            <Box sx={{ overflowX: "auto", border: "1px solid #ddd", borderRadius: "4px" }}>
              <Table size="small" style={{ minWidth: "1100px", backgroundColor: "#fff" }}>
                <TableHead>
                  <TableRow>
                    <TableCell style={{ backgroundColor: "#152F8F", color: "#fff", fontWeight: "bold", border: "1px solid #ddd" }}>Title</TableCell>
                    <TableCell style={{ backgroundColor: "#152F8F", color: "#fff", fontWeight: "bold", border: "1px solid #ddd" }}>Video ID</TableCell>
                    <TableCell style={{ backgroundColor: "#152F8F", color: "#fff", fontWeight: "bold", border: "1px solid #ddd" }}>Location</TableCell>
                    <TableCell style={{ backgroundColor: "#152F8F", color: "#fff", fontWeight: "bold", border: "1px solid #ddd" }}>Status</TableCell>
                    <TableCell style={{ backgroundColor: "#152F8F", color: "#fff", fontWeight: "bold", border: "1px solid #ddd" }}>Failures</TableCell>
                    <TableCell style={{ backgroundColor: "#152F8F", color: "#fff", fontWeight: "bold", border: "1px solid #ddd" }}>Last Attempt</TableCell>
                    <TableCell style={{ backgroundColor: "#152F8F", color: "#fff", fontWeight: "bold", border: "1px solid #ddd" }}>Last Error</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.map((item) => (
                    <TableRow key={item.video_id} hover>
                      <TableCell style={{ border: "1px solid #ddd" }}>{he.decode(item.title || item.video_id)}</TableCell>
                      <TableCell style={{ border: "1px solid #ddd" }}>{item.video_id}</TableCell>
                      <TableCell style={{ border: "1px solid #ddd" }}>{item.state} / {item.county} / {item.municipality}</TableCell>
                      <TableCell style={{ border: "1px solid #ddd" }}>
                        <Chip
                          size="small"
                          label={item.status}
                          color={statusColor[item.status] || "default"}
                        />
                      </TableCell>
                      <TableCell style={{ border: "1px solid #ddd" }}>{item.failure_count || 0}</TableCell>
                      <TableCell style={{ border: "1px solid #ddd" }}>{item.last_attempt_at || "-"}</TableCell>
                      <TableCell style={{ border: "1px solid #ddd", maxWidth: "380px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        {item.last_error || "-"}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <TablePagination
                component="div"
                count={totalItems}
                page={page}
                onPageChange={handlePageChange}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleRowsPerPageChange}
                rowsPerPageOptions={[25, 50, 100, 200]}
              />
            </Box>
          )}
        </Grid>
      </Grid>
    </>
  );
};

export default ManageVideoSummaries;
